import { useState } from 'react';
import { Layout } from 'antd';
import { Outlet } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import Sidebar from '../components/Sidebar';
import SubscriptionBanner from '../components/SubscriptionBanner';
import useAuth from '../hooks/useAuth';

const { Content } = Layout;

// Authenticated app shell.
//
// Structure (modern SaaS):
//   - <Sider>   left full-height column — owns the brand mark
//   - inner <Layout>
//       - <Header>   top bar inset to the right of the sidebar
//       - <Content>  page surface (bg-surface-50)
//
// The Sider lives directly under the outer Layout so AntD switches the
// outer Layout to flex-direction: row. The inner Layout has no Sider
// child so it stays column — Header above Content. The brand mark
// sits at the top of the sidebar (not in the topbar) so the topbar
// can stay clean: toggle on the left, tenant / role / user pills on
// the right.

export default function AppLayout() {
  const { me } = useAuth();
  // Default to collapsed so the workspace opens with maximum content
  // real estate (especially valuable for wide ERP grids). The toggle
  // in the topbar still lets users expand it on demand, and the
  // Sider's onBreakpoint handler can still collapse it back when the
  // viewport shrinks.
  const [collapsed, setCollapsed] = useState(true);

  // Sidebar is position: fixed (so the pinned Subscription / Log out
  // / status footer always stay anchored to the viewport, regardless
  // of how tall the page content gets). Because fixed elements are
  // out of the normal flex flow, the inner Layout no longer gets its
  // left offset for free — we set marginLeft equal to the current
  // Sider width and animate the change so it stays in lockstep with
  // the sidebar's own collapse animation.
  const sidebarWidth = collapsed ? 56 : 208;

  return (
    <Layout className="min-h-screen">
      <Sidebar
        collapsed={collapsed}
        onBreakpoint={(broken) => setCollapsed(broken)}
      />
      <Layout
        style={{
          marginLeft: sidebarWidth,
          transition: 'margin-left 0.2s ease',
          minHeight: '100vh',
          backgroundColor: '#EBF2FA'
        }}
      >
        <AppHeader
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed((c) => !c)}
        />
        {/* Stage UI-POLISH-LAYOUT-01 (revised): tighter side rails, wider card.
            - Horizontal padding halved (8 → 12 → 16 → 20 px) so the
              slate-blue gap between the sidebar/right-edge and the
              white card is roughly 50% thinner. The white card now
              dominates the viewport instead of floating in a margin.
            - Max-width bumped 1680 → 1920 so ultra-wide monitors get a
              proportionally bigger content area; below that, the
              w-full + reduced padding lets the card fill naturally.
            - Vertical padding stays at 24 px for clear breathing under
              the topbar. ErpMasterShell pages then cancel it for
              viewport-fit grids via their own `-my-6`. */}
        <Content className="app-content px-2 sm:px-3 lg:px-4 xl:px-5 py-6 max-w-[1920px] w-full mx-auto">
          <SubscriptionBanner subscription={me ? me.subscription : null} />
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
